// =============================================================================
// MeuDriver — LgpdRequestForm (Batch L3)
// Formulário para solicitar exportação de dados ou exclusão de conta via LGPD.
// =============================================================================

import { useState } from 'react'
import { toast } from 'sonner'
import { FileDown, Trash2, RotateCcw, Package, Clock, CheckCircle2, XCircle, Loader2 } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'

import {
  useMyLgpdRequests,
  useCreateLgpdRequest,
  type LgpdRequestType,
  type LgpdRequestStatus,
  type LgpdRequest,
} from '@/hooks/useLgpd'

// ─── Helpers ─────────────────────────────────────────────────────────────────

const REQUEST_TYPE_LABELS: Record<LgpdRequestType, string> = {
  export: 'Exportar meus dados',
  deletion: 'Excluir minha conta',
  correction: 'Corrigir meus dados',
  portability: 'Portabilidade de dados',
}

const STATUS_LABELS: Record<LgpdRequestStatus, string> = {
  pending: 'Pendente',
  processing: 'Em processamento',
  completed: 'Concluída',
  denied: 'Negada',
}

function statusVariant(status: LgpdRequestStatus): 'default' | 'secondary' | 'destructive' | 'outline' {
  switch (status) {
    case 'completed': return 'default'
    case 'denied': return 'destructive'
    case 'processing': return 'secondary'
    default: return 'outline'
  }
}

function StatusIcon({ status }: { status: LgpdRequestStatus }) {
  switch (status) {
    case 'completed': return <CheckCircle2 className="h-3.5 w-3.5" />
    case 'denied': return <XCircle className="h-3.5 w-3.5" />
    case 'processing': return <RotateCcw className="h-3.5 w-3.5 animate-spin" />
    default: return <Clock className="h-3.5 w-3.5" />
  }
}

function RequestItem({ req }: { req: LgpdRequest }) {
  return (
    <div className="flex items-center justify-between gap-3 rounded-lg border border-gray-100 bg-gray-50 px-4 py-3">
      <div className="min-w-0">
        <p className="text-sm font-medium text-gray-800">
          {REQUEST_TYPE_LABELS[req.request_type]}
        </p>
        <p className="text-xs text-gray-400 mt-0.5">
          {new Date(req.requested_at).toLocaleDateString('pt-BR', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
          })}
        </p>
        {req.notes && (
          <p className="text-xs text-gray-500 mt-1 italic">{req.notes}</p>
        )}
      </div>
      <Badge variant={statusVariant(req.status)} className="shrink-0 gap-1 text-xs">
        <StatusIcon status={req.status} />
        {STATUS_LABELS[req.status]}
      </Badge>
    </div>
  )
}

// ─── useCreateLgpdRequest hook (re-export para uso interno) ──────────────────

function useLgpdAction() {
  const { mutateAsync, isPending } = useCreateLgpdRequest()
  return { mutateAsync, isPending }
}

// ─── Main component ───────────────────────────────────────────────────────────

export function LgpdRequestForm() {
  const { data: requests, isLoading } = useMyLgpdRequests()
  const { mutateAsync, isPending } = useLgpdAction()
  const [loadingType, setLoadingType] = useState<LgpdRequestType | null>(null)

  async function handleRequest(type: LgpdRequestType) {
    setLoadingType(type)
    try {
      await mutateAsync(type)
      toast.success('Solicitação enviada! Retornaremos em até 15 dias úteis.')
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Erro ao enviar solicitação.'
      toast.error(msg)
    } finally {
      setLoadingType(null)
    }
  }

  const hasPendingExport = requests?.some(
    (r) => r.request_type === 'export' && r.status === 'pending',
  )
  const hasPendingDeletion = requests?.some(
    (r) => r.request_type === 'deletion' && (r.status === 'pending' || r.status === 'processing'),
  )

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <Package className="h-4 w-4 text-primary" />
          Direitos LGPD
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-5">
        <p className="text-sm text-muted-foreground">
          De acordo com a Lei Geral de Proteção de Dados (Lei 13.709/2018), você pode solicitar
          acesso, portabilidade ou exclusão dos seus dados. Respondemos em até 15 dias úteis.
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {/* Exportar dados */}
          <div className="rounded-xl border border-gray-100 p-4 space-y-2">
            <div className="flex items-center gap-2">
              <FileDown className="h-4 w-4 text-brand-600" />
              <p className="text-sm font-medium text-gray-800">Exportar meus dados</p>
            </div>
            <p className="text-xs text-muted-foreground">
              Receba um arquivo com todos os seus dados pessoais armazenados na plataforma.
            </p>
            <Button
              size="sm"
              variant="outline"
              disabled={hasPendingExport || (isPending && loadingType === 'export')}
              onClick={() => handleRequest('export')}
              className="w-full gap-2 mt-1"
              data-testid="lgpd-export-btn"
            >
              {isPending && loadingType === 'export' ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <FileDown className="h-3.5 w-3.5" />
              )}
              {hasPendingExport ? 'Solicitação enviada' : 'Solicitar exportação'}
            </Button>
          </div>

          {/* Excluir conta */}
          <div className="rounded-xl border border-red-100 bg-red-50/30 p-4 space-y-2">
            <div className="flex items-center gap-2">
              <Trash2 className="h-4 w-4 text-red-600" />
              <p className="text-sm font-medium text-gray-800">Excluir minha conta</p>
            </div>
            <p className="text-xs text-muted-foreground">
              Solicita a exclusão permanente de todos os seus dados. Esta ação é irreversível.
            </p>
            <Button
              size="sm"
              variant="outline"
              disabled={hasPendingDeletion || (isPending && loadingType === 'deletion')}
              onClick={() => handleRequest('deletion')}
              className="w-full gap-2 mt-1 border-red-200 text-red-700 hover:bg-red-50"
              data-testid="lgpd-deletion-btn"
            >
              {isPending && loadingType === 'deletion' ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Trash2 className="h-3.5 w-3.5" />
              )}
              {hasPendingDeletion ? 'Em processamento' : 'Solicitar exclusão'}
            </Button>
          </div>
        </div>

        {/* Histórico de solicitações */}
        {isLoading ? (
          <div className="flex items-center justify-center py-4">
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
          </div>
        ) : requests && requests.length > 0 ? (
          <div className="space-y-2 pt-2 border-t">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
              Histórico de solicitações
            </p>
            {requests.map((req) => (
              <RequestItem key={req.id} req={req} />
            ))}
          </div>
        ) : null}
      </CardContent>
    </Card>
  )
}
